import React, { useState } from "react";
import { Attention, Badge, TabNav, Typography } from "@optimizely/axiom";

import styles from "./BulkUploadDetails.module.scss";
import { BulkUpload } from "../../../domain/BulkUpload";
import { BulkUploadAccessTab } from "./BulkUploadAccessTab";
import { BulkUploadFailuresTab } from "./BulkUploadFailuresTab";
import { BULK_UPLOAD_RESULT_STATUS } from "../../../constants/constants";
import {
    EMPTY_VALUE,
    formatCount,
    formatProgress,
    formatResultStatus,
    formatTimestamp
} from "../../../lib/bulk-upload-format";
import { getFormattedDuration } from "../../../lib/date-formatter";
import { TableSkeleton } from "../Skeleton/TableSkeleton";

export const UPLOAD_DETAILS_TAB = {
    DETAILS: "details",
    ACCESS: "access",
    FAILURES: "failures"
} as const;

type UploadDetailsTab = (typeof UPLOAD_DETAILS_TAB)[keyof typeof UPLOAD_DETAILS_TAB];

const TABS: { id: UploadDetailsTab; label: string }[] = [
    { id: UPLOAD_DETAILS_TAB.DETAILS, label: "Details" },
    { id: UPLOAD_DETAILS_TAB.ACCESS, label: "Access" },
    { id: UPLOAD_DETAILS_TAB.FAILURES, label: "Failures" }
];

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className={styles["upload-details__row"]}>
        <label className="oui-label">{label}</label>
        <Typography type="body" className={styles["upload-details__value"]}>
            {children}
        </Typography>
    </div>
);

/**
 * Banner summarising where the upload got to. Until the job writes a result there is no outcome to
 * report, so an unprocessed upload gets a neutral "in progress" note rather than a red one, and a
 * partial one gets a warning rather than being reported as an outright failure.
 */
const StatusBanner = ({ status, resultStatus }: Pick<BulkUpload, "status" | "resultStatus">) => {
    if (!resultStatus) {
        return (
            <Attention alignment="left" type="warning" className="push-double--bottom">
                <span data-testid="upload-details-status">
                    <strong>Status:</strong> {status ?? EMPTY_VALUE} — processing has not finished yet.
                </span>
            </Attention>
        );
    }

    const bannerType =
        resultStatus === BULK_UPLOAD_RESULT_STATUS.SUCCEEDED
            ? "good-news"
            : resultStatus === BULK_UPLOAD_RESULT_STATUS.PARTIALLY_SUCCEEDED
            ? "warning"
            : "bad-news";

    // `Failed` is the only value the lifecycle and outcome vocabularies share, so it is the only
    // pairing that reads as "Failed — Failed". There is no second fact in that, so it is said once.
    // Compared on the wire values, displayed in prose.
    const outcome = formatResultStatus(resultStatus);
    const label = !status || status === resultStatus ? outcome : `${status} — ${outcome}`;

    return (
        <Attention alignment="left" type={bannerType} className="push-double--bottom">
            <span data-testid="upload-details-status">
                <strong>Status:</strong> {label}
            </span>
        </Attention>
    );
};

/** File metadata and record counts — the default tab. */
const DetailsTab = ({ upload }: { upload: BulkUpload }) => {
    const {
        originalFileName,
        uploaderEmail,
        uploadedAt,
        recordCount,
        processedRowCount,
        successCount,
        failedCount,
        startTime,
        endTime
    } = upload;

    return (
        <div data-testid="upload-details-summary">
            <div className={styles["upload-details__section"]}>
                <DetailRow label="File Name">{originalFileName || EMPTY_VALUE}</DetailRow>
                <DetailRow label="Uploaded By">{uploaderEmail || EMPTY_VALUE}</DetailRow>
                <DetailRow label="Uploaded">{formatTimestamp(uploadedAt)}</DetailRow>
                <DetailRow label="Processing Started">{formatTimestamp(startTime)}</DetailRow>
                <DetailRow label="Processing Ended">{formatTimestamp(endTime)}</DetailRow>
                <DetailRow label="Duration">
                    {getFormattedDuration({ start: startTime, end: endTime }) ?? EMPTY_VALUE}
                </DetailRow>
            </div>

            <div className={styles["upload-details__section"]}>
                <Typography tag="h4" type="header4" className="push--bottom">
                    Records
                </Typography>
                <DetailRow label="Processed">{formatProgress(processedRowCount, recordCount)}</DetailRow>
                <DetailRow label="Succeeded">
                    <Badge color="live">{formatCount(successCount)}</Badge>
                </DetailRow>
                <DetailRow label="Failed">
                    {/* Red only when something actually failed — a zero stays neutral so the section
                        does not read as an alarm on a clean import. */}
                    <Badge color={failedCount ? "bad-news" : undefined}>{formatCount(failedCount)}</Badge>
                </DetailRow>
            </div>
        </div>
    );
};

/**
 * Full record for one bulk user CSV upload, rendered inside the Uploads tab sidebar.
 *
 * The caller owns the fetch so this stays a presentational component: it renders whatever it is given,
 * including the partial record an in-flight upload produces. The Access tab is the one exception — it
 * resolves the recorded group ids itself, because ids alone say nothing about the access granted.
 *
 * The loading / error guards sit outside the tabs so a failed fetch shows one message rather than
 * three empty panels.
 */
export const BulkUploadDetails = ({
    upload,
    isLoading,
    error
}: {
    upload?: BulkUpload;
    isLoading: boolean;
    error: boolean;
}) => {
    const [currentTab, setCurrentTab] = useState<UploadDetailsTab>(UPLOAD_DETAILS_TAB.DETAILS);

    if (isLoading) {
        return (
            <div className="push-double--top">
                <TableSkeleton fullWidth />
            </div>
        );
    }

    // Also covers a 404: a file id that is not this organization's resolves to no upload, and there is
    // nothing more useful to say about it than that it could not be loaded.
    if (error || !upload) {
        return (
            <div data-testid="upload-details-error">
                Failed to load this upload&apos;s details. Please try again later.
            </div>
        );
    }

    const {
        status,
        resultStatus,
        failedCount,
        failedEmails,
        failedEmailsRetained,
        appliedUserGroupIds,
        rejectedUserGroupIds
    } = upload;

    return (
        <div className={styles["upload-details"]} data-testid="upload-details">
            <StatusBanner status={status} resultStatus={resultStatus} />

            <TabNav
                activeTab={currentTab}
                className={`push-double--bottom ${styles["upload-details__tabs"]}`}
                style={["dashboard"]}
                data-testid="upload-details-tabs"
                data-cy="upload-details-tabs"
            >
                {TABS.map(({ id, label }) => (
                    // The handler lives only on `Tab`, which renders the `<li>`: a click on the inner
                    // button bubbles up to it, so handling it in both places would fire twice.
                    <TabNav.Tab
                        key={id}
                        tabId={id}
                        className={styles["upload-details__tab"]}
                        onClick={(event: React.SyntheticEvent) => {
                            event.preventDefault();
                            setCurrentTab(id);
                        }}
                    >
                        <button
                            className={styles["upload-details__tab-button"]}
                            // Prefixed because ids are global and `details-tab` is already taken by
                            // UserGroupFormTabs.
                            id={`upload-details-${id}-tab`}
                            data-testid={`upload-details-${id}-tab`}
                            data-cy={`upload-details-${id}-tab`}
                            role="tab"
                            tabIndex={-1}
                            type="button"
                        >
                            {label}
                        </button>
                    </TabNav.Tab>
                ))}
            </TabNav>

            {currentTab === UPLOAD_DETAILS_TAB.DETAILS && <DetailsTab upload={upload} />}

            {currentTab === UPLOAD_DETAILS_TAB.ACCESS && (
                <BulkUploadAccessTab
                    appliedUserGroupIds={appliedUserGroupIds}
                    rejectedUserGroupIds={rejectedUserGroupIds}
                />
            )}

            {currentTab === UPLOAD_DETAILS_TAB.FAILURES && (
                <BulkUploadFailuresTab
                    failedEmails={failedEmails}
                    failedEmailsRetained={failedEmailsRetained}
                    failedCount={failedCount}
                    resultStatus={resultStatus}
                />
            )}
        </div>
    );
};

BulkUploadDetails.displayName = "BulkUploadDetails";
